/**
 * ModelSelector — Shared AI model selection component
 *
 * Reusable dropdown for selecting AI model across the application.
 * Used by CreateInsightsDialog and other AI forms.
 *
 * Supports Anthropic, OpenAI, Google Gemini, and Perplexity models with
 * auto-derived provider.
 */

import React from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export type ModelSelectorProvider = 'anthropic' | 'openai' | 'google' | 'perplexity';
export type ModelUsageFilter = 'general' | 'vision' | 'deep-research' | 'deep-research-only' | 'all';

export interface ModelOption {
  value: string;
  label: string;
  provider: ModelSelectorProvider;
  deepResearchOnly?: boolean;
  deepResearchCapable?: boolean;
  visionCapable?: boolean;
}

/**
 * Static fallback model catalog. Runtime surfaces should prefer passing
 * `options` from the availability API so unavailable providers are hidden.
 */
export const MODEL_OPTIONS: ReadonlyArray<ModelOption> = [
  // Anthropic — prices per 1M tokens.
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 ($1/$5, Fast)', provider: 'anthropic' as const, visionCapable: true },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5 ($3/$15, Recommended)', provider: 'anthropic' as const, visionCapable: true },
  { value: 'claude-opus-5', label: 'Claude Opus 5 ($5/$25, Best Quality)', provider: 'anthropic' as const, visionCapable: true, deepResearchCapable: true },
  { value: 'claude-fable-5', label: 'Claude Fable 5 ($10/$50, Most Capable)', provider: 'anthropic' as const, visionCapable: true, deepResearchCapable: true },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 ($3/$15, Legacy)', provider: 'anthropic' as const, visionCapable: true },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 ($5/$25, Legacy)', provider: 'anthropic' as const, visionCapable: true },
  // OpenAI — GPT-5.6 family. It also replaces the retired o3/o4-mini
  // deep-research models, which shut down 2026-07-23; unlike them, Sol does
  // grounded search AND structured output in a single call.
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra ($2/$12)', provider: 'openai' as const, visionCapable: true },
  { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol ($5/$30, Deep Research)', provider: 'openai' as const, visionCapable: true, deepResearchCapable: true },
  { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna ($0.20/$1.20, Fast)', provider: 'openai' as const, visionCapable: true },
  // Google Gemini — prices shown as input/output per 1M tokens.
  // Gemini 3-only: 2.5 models had structured-output compatibility issues
  // with the legacy `responseSchema` API field. Gemini 3 uses the newer
  // `responseJsonSchema` which is more reliable.
  { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash ($0.75/$3.75)', provider: 'google' as const, visionCapable: true },
  { value: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite ($0.30/$2.50)', provider: 'google' as const, visionCapable: true },
  {
    value: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro Preview ($2/$12)',
    provider: 'google' as const,
    deepResearchCapable: true,
    visionCapable: true,
  },
  // Perplexity Sonar — web-grounded research, 1-pass grounded + structured.
  // Prices per 1M tokens (+ per-request search fee, verify at perplexity.ai/settings/pricing).
  { value: 'sonar', label: 'Sonar ($1/$1)', provider: 'perplexity' as const },
  {
    value: 'sonar-pro',
    label: 'Sonar Pro ($3/$15) — Recommended for research',
    provider: 'perplexity' as const,
    deepResearchCapable: true,
  },
  { value: 'sonar-reasoning', label: 'Sonar Reasoning ($1/$5)', provider: 'perplexity' as const },
  { value: 'sonar-reasoning-pro', label: 'Sonar Reasoning Pro ($2/$8)', provider: 'perplexity' as const },
  {
    value: 'sonar-deep-research',
    label: 'Sonar Deep Research ($2/$8)',
    provider: 'perplexity' as const,
    deepResearchOnly: true,
    deepResearchCapable: true,
  },
] as const;

export const DEFAULT_MODEL = 'claude-sonnet-5';

/**
 * Frontend mirror of `ai-core/src/modelRouting.ts` MODEL_ID_ALIASES.
 * Both files MUST stay in sync — the backend resolves the id it actually
 * calls, this copy keeps the dropdown from showing "Select a connected model"
 * for a project still pinned to a retired id.
 */
const MODEL_ID_ALIASES: Record<string, string> = {
  'gemini-3.7-flash': 'gemini-3.8-flash',
  'gemini-3.6-flash': 'gemini-3.8-flash',
  'gemini-3.5-flash': 'gemini-3.8-flash',
  'gemini-3-flash-preview': 'gemini-3.8-flash',
  'gemini-3.1-flash-lite': 'gemini-3.5-flash-lite',
  'gemini-3.1-flash-lite-preview': 'gemini-3.5-flash-lite',

  'gpt-5.5': 'gpt-5.6-sol',
  'gpt-5.4': 'gpt-5.6-terra',
  'gpt-5.4-mini': 'gpt-5.6-luna',
  'gpt-5-mini': 'gpt-5.6-luna',
  'gpt-4o': 'gpt-5.6-terra',
  'gpt-4o-mini': 'gpt-5.6-luna',
  'o3-mini': 'gpt-5.6-luna',
  'o3-deep-research': 'gpt-5.6-sol',
  'o4-mini-deep-research': 'gpt-5.6-sol',

  'claude-sonnet-4-5-20250929': 'claude-sonnet-4-6',
  'claude-sonnet-4-20250514': 'claude-sonnet-4-6',
  'claude-opus-4-5-20250514': 'claude-opus-4-6',
  'claude-opus-4-5-20251101': 'claude-opus-4-6',
};

export function normalizeModelId(modelId: string): string {
  return MODEL_ID_ALIASES[modelId] ?? modelId;
}

const MODEL_CATALOG_ORDER = new Map(
  MODEL_OPTIONS.map((option, index) => [normalizeModelId(option.value), index]),
);

export function getStaticModelOption(modelId: string): ModelOption | undefined {
  const normalized = normalizeModelId(modelId);
  return MODEL_OPTIONS.find((option) => normalizeModelId(option.value) === normalized);
}

export function sortModelOptionsByCatalog<T extends ModelOption>(
  options: ReadonlyArray<T>,
): T[] {
  return [...options].sort((a, b) => {
    const aOrder = MODEL_CATALOG_ORDER.get(normalizeModelId(a.value)) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = MODEL_CATALOG_ORDER.get(normalizeModelId(b.value)) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.label.localeCompare(b.label);
  });
}

export function filterModelOptionsByUsage<T extends ModelOption>(
  options: ReadonlyArray<T>,
  modelUsage: ModelUsageFilter = 'general',
): T[] {
  if (modelUsage === 'all') return [...options];
  if (modelUsage === 'deep-research-only') {
    return options.filter((option) => option.deepResearchOnly);
  }
  if (modelUsage === 'deep-research') {
    return options.filter((option) => option.deepResearchCapable || option.deepResearchOnly);
  }
  if (modelUsage === 'vision') {
    return options.filter((option) => option.visionCapable && !option.deepResearchOnly);
  }
  return options.filter((option) => !option.deepResearchOnly);
}

/** Derive AI provider from model ID. */
export function getProviderForModel(modelId: string): 'anthropic' | 'google' | 'openai' | 'perplexity' {
  const normalized = normalizeModelId(modelId);
  const option = MODEL_OPTIONS.find(o => o.value === normalized);
  if (option) return option.provider;
  if (normalized.startsWith('gemini')) return 'google';
  if (normalized.startsWith('gpt') || normalized.startsWith('o3') || normalized.startsWith('o4')) return 'openai';
  if (normalized === 'sonar' || normalized.startsWith('sonar-')) return 'perplexity';
  return 'anthropic';
}

export interface ModelSelectorProps {
  value: string;
  onChange: (model: string) => void;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  label?: string;
  testId?: string;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  onOpen?: () => void;
  /**
   * Default 'general' hides deep-research-only models from normal generation
   * surfaces. Use 'deep-research' or 'deep-research-only' for research flows.
   */
  modelUsage?: ModelUsageFilter;
  /**
   * Available options for the current project. When omitted, the static
   * fallback catalog above is used.
   */
  options?: ReadonlyArray<ModelOption>;
  /**
   * Restrict the dropdown to specific providers. Default: all providers shown.
   * Pass a subset like `['google']` to show only Gemini models — useful for
   * features that require structured-output support (currently only Gemini).
   */
  providers?: ModelSelectorProvider[];
}

const PROVIDER_LABEL: Record<ModelSelectorProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google Gemini',
  perplexity: 'Perplexity (Sonar)',
};

const ALL_PROVIDERS: ModelSelectorProvider[] = ['anthropic', 'openai', 'google', 'perplexity'];

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  value,
  onChange,
  size = 'small',
  sx,
  label = 'Model',
  testId,
  disabled = false,
  loading = false,
  fullWidth = false,
  onOpen,
  modelUsage = 'general',
  options,
  providers,
}) => {
  const visibleProviders = providers ?? ALL_PROVIDERS;
  const selectedValue = normalizeModelId(value);
  const availableOptions = options ?? MODEL_OPTIONS;
  const filteredOptions = filterModelOptionsByUsage(
    availableOptions.filter((option) => visibleProviders.includes(option.provider)),
    modelUsage,
  );
  const hasSelectedValue = filteredOptions.some((option) => option.value === selectedValue);
  const selectValue = hasSelectedValue ? selectedValue : '';
  const reactId = React.useId();
  const labelId = `model-select-label-${reactId}`;
  const selectId = `model-select-${reactId}`;
  return (
    <FormControl
      size={size}
      sx={{ minWidth: 280, position: 'relative', ...sx as any }}
      data-testid={testId}
      fullWidth={fullWidth}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        value={selectValue}
        label={label}
        disabled={disabled || (!loading && filteredOptions.length === 0)}
        onOpen={onOpen}
        onChange={(e) => onChange(String(e.target.value))}
      >
        {visibleProviders.flatMap((provider) => {
          const items = filteredOptions.filter((o) => o.provider === provider);
          if (items.length === 0) return [];
          // When only one provider is visible, skip the subheader for cleaner UI
          const showSubheader = visibleProviders.length > 1;
          return [
            ...(showSubheader
              ? [
                  <ListSubheader key={`subheader-${provider}`}>
                    {PROVIDER_LABEL[provider]}
                  </ListSubheader>,
                ]
              : []),
            ...items.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            )),
          ];
        })}
        {!hasSelectedValue ? (
          <MenuItem value="" disabled>
            {filteredOptions.length === 0 ? 'No connected models' : 'Select a connected model'}
          </MenuItem>
        ) : null}
      </Select>
      {loading ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            pointerEvents: 'none',
            position: 'absolute',
            right: 34,
            top: 0,
          }}
        >
          <CircularProgress color="inherit" size={18} />
        </Box>
      ) : null}
    </FormControl>
  );
};
